import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'
import { serverMessage } from '@/Ship/Helpers/serverError'

/** Дітей (договорів, документів) в одного власника одиниці; з великим запасом — одним запитом. */
export const CRM_CHILD_LIST_LIMIT = 100

/** Права рядка дитини — `_permissions` із `?permissions=true`. */
export interface CrmChildPermissions {
    read?: boolean
    update?: boolean
    delete?: boolean
}

/** Мінімум рядка дитини, з яким працює вкладка; решту полів малюють колонки. */
export interface CrmChildRow {
    uuid: string
    '@id'?: string
    _permissions?: CrmChildPermissions
    [key: string]: unknown
}

type CollectionResponse<T> = { items?: T[]; 'hydra:member'?: T[]; meta?: { total?: number } } | T[] | null

/**
 * Список дітей одного власника CRM для вкладки картки (`SPEC-008` §7.1):
 * невидалені записи, новіші першими (так віддає бек, `SPEC-008` §5.1).
 *
 * Відбирає записи префільтром `<поле власника>.uuid` спільної колекції
 * (`/services/client_contracts`, `/services/client_documents`) — окремого
 * ендпойнта на власника немає. Права рядка — `_permissions`.
 */
export class CrmChildListStore<T extends CrmChildRow = CrmChildRow> {
    items: T[] = []
    /** Скільки всього: якщо більше, ніж показано, вкладка про це скаже */
    total = 0
    isLoading = false
    failed = false
    removingUuid: string | null = null

    constructor(
        private readonly restUrl: string,
        private readonly ownerField: string
    ) {
        makeAutoObservable(this, {}, { autoBind: true })
    }

    /**
     * `forceRefresh`: ядровий клієнт дедуплікує GET на 30 с, і без нього
     * щойно доданий запис не з'явився б у списку.
     */
    async load(ownerUuid: string | undefined): Promise<void> {
        if (!ownerUuid) return

        this.isLoading = true
        this.failed = false

        try {
            const filters = JSON.stringify({ and: [{ field: `${this.ownerField}.uuid`, op: '=', value: ownerUuid }] })
            const raw = await restClient.get(
                `${this.restUrl}?limit=${CRM_CHILD_LIST_LIMIT}&permissions=true&filters=${encodeURIComponent(filters)}`,
                undefined,
                { forceRefresh: true }
            )
            const data = raw as unknown as CollectionResponse<T>
            const items = Array.isArray(data) ? data : (data?.items ?? data?.['hydra:member'] ?? [])
            const rows = Array.isArray(items) ? items.filter((item) => typeof item?.uuid === 'string') : []

            runInAction(() => {
                this.items = rows
                this.total = !Array.isArray(data) && typeof data?.meta?.total === 'number' ? data.meta.total : rows.length
            })
        } catch {
            runInAction(() => {
                this.items = []
                this.total = 0
                this.failed = true
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    /**
     * Видаляє запис (м'яко, `SPEC-008` §5.2) і перечитує список.
     * Підтвердження — справа вкладки (`Popconfirm`). Повертає текст помилки
     * або `null`.
     */
    async remove(uuid: string, ownerUuid: string): Promise<string | null> {
        if (this.removingUuid !== null) return null

        this.removingUuid = uuid

        try {
            await restClient.delete(`${this.restUrl}/${encodeURIComponent(uuid)}`)
            await this.load(ownerUuid)

            return null
        } catch (error) {
            return serverMessage(error, 'Зверніться до адміністратора системи.')
        } finally {
            runInAction(() => {
                this.removingUuid = null
            })
        }
    }
}
